import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage, defineMessages } from "react-intl";

import {
  useAuthorizeExternalSession,
  useCreateExternalSession,
} from "@/api/sessions";
import {
  Button,
  Form,
  FormActions,
  FormGroup,
  Input,
  Spinner,
  Text,
} from "@/components/ui";
import { LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY } from "@/contexts/auth/utils";

const messages = defineMessages({
  instanceLabel: {
    id: "login.fields.instance_label",
    defaultMessage: "Instance",
  },
  instancePlaceholder: {
    id: "login.fields.instance_placeholder",
    defaultMessage: "example.com",
  },
});

/** Form for logging into a remote instance */
const ExternalLoginForm: React.FC = () => {
  const query = new URLSearchParams(window.location.search);
  const code = query.get("code");

  const intl = useIntl();
  const authorizeExternalSession = useAuthorizeExternalSession();
  const createExternalSession = useCreateExternalSession();

  const [host, setHost] = useState("");

  const handleHostChange: React.ChangeEventHandler<HTMLInputElement> = ({
    currentTarget,
  }) => {
    setHost(currentTarget.value);
  };

  const handleSubmit = () => {
    localStorage.setItem(
      LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY,
      `https://${host}`,
    );
    authorizeExternalSession.mutate({ host });
  };

  useEffect(() => {
    if (code) {
      createExternalSession.mutate({ code });
    }
  }, [code]);

  if (code) {
    return <Spinner />;
  }

  return (
    <Form onSubmit={handleSubmit} data-testid="external-login">
      <FormGroup labelText={intl.formatMessage(messages.instanceLabel)}>
        <Input
          aria-label={intl.formatMessage(messages.instancePlaceholder)}
          placeholder={intl.formatMessage(messages.instancePlaceholder)}
          prepend={
            <Text theme="muted" size="sm" className="pl-2.5">
              https://
            </Text>
          }
          type="text"
          name="host"
          onChange={handleHostChange}
          autoCorrect="off"
          autoCapitalize="off"
          required
        />
      </FormGroup>

      <FormActions>
        <Button
          variant="default"
          type="submit"
          disabled={
            authorizeExternalSession.isLoading ||
            createExternalSession.isLoading
          }
        >
          <FormattedMessage id="login.log_in" defaultMessage="Log in" />
        </Button>
      </FormActions>
    </Form>
  );
};

export default ExternalLoginForm;
